// frontend/src/pages/EmailMarketing.jsx
import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import axios from "../utils/axios";

function EmailMarketing() {
  // Campaign sending state
  const [campaignData, setCampaignData] = useState({
    subject:
      "Transform Your CRP Operations with WebVenly - Free Demo Available",
    campaign_name: "",
    recipient_type: "manual",
    manual_emails: "",
    selected_subscriber_ids: [],
    role_filter: "",
    exclude_unsubscribed: true,
  });

  // UI state
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState("compose");
  const [showPreview, setShowPreview] = useState(false);
  const [emailHTML, setEmailHTML] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);

  // New subscriber form
  const [newSubscriber, setNewSubscriber] = useState({
    email: "",
    name: "",
    organization: "",
    role: "crp_vendor",
  });
  const [addingSubscriber, setAddingSubscriber] = useState(false);

  // Data state
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [unsubscribed, setUnsubscribed] = useState([]);
  const [selectedSubscribers, setSelectedSubscribers] = useState(new Set());

  // Filters and pagination
  const [subscriberFilters, setSubscriberFilters] = useState({
    role: "all",
    search: "",
    active_only: "all", // Changed from "true" to "all" to show both active and inactive
  });
  const [loading, setLoading] = useState(false);

  // Load initial data
  useEffect(() => {
    if (activeTab === "subscribers") {
      loadSubscribers();
    } else if (activeTab === "campaigns") {
      loadCampaigns();
    } else if (activeTab === "unsubscribed") {
      loadUnsubscribed();
    }
    loadStats();
  }, [activeTab, subscriberFilters]);

  const loadSubscribers = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/email/subscribers", {
        params: subscriberFilters,
      });
      if (response.data.success) {
        setSubscribers(response.data.subscribers);
      }
    } catch (error) {
      console.error("Error loading subscribers:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/email/campaigns");
      if (response.data.success) {
        setCampaigns(response.data.campaigns);
      }
    } catch (error) {
      console.error("Error loading campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnsubscribed = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/email/unsubscribed", {
        params: {
          search: subscriberFilters.search,
          limit: 100,
        },
      });
      if (response.data.success) {
        setUnsubscribed(response.data.unsubscribed);
      }
    } catch (error) {
      console.error("Error loading unsubscribed users:", error);
      // Fallback: create mock data if endpoint doesn't exist yet
      setUnsubscribed([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get("/api/email/marketing-stats");
      if (response.data.success) {
        setSubscriberStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  // Fetch the email template when preview is requested
  const fetchEmailTemplate = async () => {
    try {
      setLoadingTemplate(true);
      const response = await axios.get("/api/email/template");
      if (response.data.success) {
        setEmailHTML(response.data.template);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error fetching template:", err);
      alert("Failed to load email template preview");
      return false;
    } finally {
      setLoadingTemplate(false);
    }
  };

  const previewEmail = async () => {
    if (!showPreview && !emailHTML) {
      // Fetch template if not already loaded
      const success = await fetchEmailTemplate();
      if (success) {
        setShowPreview(true);
      }
    } else {
      setShowPreview(!showPreview);
    }
  };

  const downloadTemplate = async () => {
    // Ensure template is loaded
    if (!emailHTML) {
      const success = await fetchEmailTemplate();
      if (!success) return;
    }

    const blob = new Blob([emailHTML], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "webvenly-email-template.html";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const addSubscriber = async () => {
    if (!newSubscriber.email.trim()) {
      alert("Email address is required");
      return;
    }

    if (!newSubscriber.email.includes("@")) {
      alert("Please enter a valid email address");
      return;
    }

    try {
      setAddingSubscriber(true);
      const response = await axios.post("/api/email/subscribe", {
        ...newSubscriber,
        source: "manual_admin",
      });

      if (response.data.success) {
        alert("Subscriber added successfully!");
        setNewSubscriber({
          email: "",
          name: "",
          organization: "",
          role: "crp_vendor",
        });
        setShowAddSubscriber(false);
        // Refresh subscribers list and stats
        if (activeTab === "subscribers") {
          loadSubscribers();
        }
        loadStats();
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (error) {
      console.error("Error adding subscriber:", error);
      alert(
        `Error adding subscriber: ${
          error.response?.data?.error || "Unknown error"
        }`
      );
    } finally {
      setAddingSubscriber(false);
    }
  };

  const bulkImportSubscribers = async (csvData) => {
    try {
      setLoading(true);
      const response = await axios.post("/api/email/bulk-import", {
        subscribers: csvData,
      });

      if (response.data.success) {
        alert(
          `Bulk import completed!\nSuccessful: ${response.data.results.successful}\nFailed: ${response.data.results.failed}`
        );
        // Refresh data
        loadSubscribers();
        loadStats();
      }
    } catch (error) {
      console.error("Error with bulk import:", error);
      alert(
        `Bulk import failed: ${error.response?.data?.error || "Unknown error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleSubscriberStatus = async (subscriberId, currentStatus) => {
    const action = currentStatus ? "unsubscribe" : "resubscribe";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      const response = await axios.put(
        `/api/email/subscribers/${subscriberId}/toggle`,
        {
          is_active: !currentStatus,
        }
      );

      if (response.data.success) {
        // Refresh data
        loadSubscribers();
        loadStats();
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (error) {
      console.error("Error toggling subscriber status:", error);
      alert(
        `Error toggling subscriber status: ${
          error.response?.data?.error || "Unknown error"
        }`
      );
    }
  };

  const handleSubscriberSelect = (subscriberId, checked) => {
    const newSelected = new Set(selectedSubscribers);
    if (checked) {
      newSelected.add(subscriberId);
    } else {
      newSelected.delete(subscriberId);
    }
    setSelectedSubscribers(newSelected);
  };

  const selectAllSubscribers = (checked) => {
    if (checked) {
      setSelectedSubscribers(new Set(subscribers.map((s) => s.id)));
    } else {
      setSelectedSubscribers(new Set());
    }
  };

  const getEstimatedRecipients = () => {
    switch (campaignData.recipient_type) {
      case "manual":
        return campaignData.manual_emails
          .split(/[,\n\r]/)
          .filter(
            (email) =>
              email.trim() && email.includes("@") && email.includes(".")
          ).length;
      case "all_active":
        return subscriberStats.active_subscribers || 0;
      case "by_role":
        const roleMap = {
          crp_vendor: subscriberStats.crp_vendors || 0,
          state_agency: subscriberStats.state_agencies || 0,
          job_coach: subscriberStats.job_coaches || 0,
        };
        return roleMap[campaignData.role_filter] || 0;
      case "selected_ids":
        return selectedSubscribers.size;
      default:
        return 0;
    }
  };

  const sendCampaign = async () => {
    if (!campaignData.subject.trim()) {
      alert("Please enter an email subject");
      return;
    }

    const estimatedRecipients = getEstimatedRecipients();
    if (estimatedRecipients === 0) {
      alert("No recipients selected");
      return;
    }

    if (
      !window.confirm(`Send campaign to ${estimatedRecipients} recipients?`)
    ) {
      return;
    }

    try {
      setSending(true);

      // Prepare campaign data
      const campaignPayload = {
        ...campaignData,
        campaign_name:
          campaignData.campaign_name ||
          `Campaign ${new Date().toLocaleDateString()}`,
        manual_emails:
          campaignData.recipient_type === "manual"
            ? campaignData.manual_emails
                .split(/[,\n\r]/)
                .filter(
                  (email) =>
                    email.trim() && email.includes("@") && email.includes(".")
                )
                .map((email) => email.trim())
            : [],
        selected_subscriber_ids:
          campaignData.recipient_type === "selected_ids"
            ? Array.from(selectedSubscribers)
            : [],
      };

      const response = await axios.post(
        "/api/email/send-marketing-emails",
        campaignPayload
      );

      if (response.data.success) {
        alert(
          `Campaign sent successfully!\nSent: ${response.data.results.successful}\nFailed: ${response.data.results.failed}`
        );

        // Reset form
        setCampaignData({
          ...campaignData,
          manual_emails: "",
          campaign_name: "",
        });
        setSelectedSubscribers(new Set());

        // Refresh stats
        loadStats();
      }
    } catch (error) {
      console.error("Error sending campaign:", error);
      alert(
        `Error sending campaign: ${
          error.response?.data?.error || "Unknown error"
        }`
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="container py-5">
        <div className="text-center mb-5">
          <h1 className="display-5 fw-bold mb-3">Email Marketing Center</h1>
          <p className="lead text-muted">
            Manage subscribers and send targeted campaigns
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <h5 className="card-title">Active Subscribers</h5>
                <h2>{subscriberStats.active_subscribers || 0}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <h5 className="card-title">Campaigns Sent</h5>
                <h2>{subscriberStats.campaigns_sent || 0}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <h5 className="card-title">Total Emails Sent</h5>
                <h2>{subscriberStats.total_emails_sent || 0}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body">
                <h5 className="card-title">Unsubscribes</h5>
                <h2>{subscriberStats.total_unsubscribes || 0}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4" role="tablist">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "compose" ? "active" : ""}`}
              onClick={() => setActiveTab("compose")}
            >
              Compose Campaign
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${
                activeTab === "subscribers" ? "active" : ""
              }`}
              onClick={() => setActiveTab("subscribers")}
            >
              Manage Subscribers ({subscriberStats.active_subscribers || 0}{" "}
              active)
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "template" ? "active" : ""}`}
              onClick={() => setActiveTab("template")}
            >
              Email Template
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${
                activeTab === "campaigns" ? "active" : ""
              }`}
              onClick={() => setActiveTab("campaigns")}
            >
              Campaign History
            </button>
          </li>
        </ul>

        {/* Compose Campaign Tab */}
        {activeTab === "compose" && (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Create New Campaign</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Campaign Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={campaignData.campaign_name}
                      onChange={(e) =>
                        setCampaignData({
                          ...campaignData,
                          campaign_name: e.target.value,
                        })
                      }
                      placeholder="e.g., January 2025 Newsletter"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Email Subject</label>
                    <input
                      type="text"
                      className="form-control"
                      value={campaignData.subject}
                      onChange={(e) =>
                        setCampaignData({
                          ...campaignData,
                          subject: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Recipients</label>
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="recipient_type"
                        value="manual"
                        checked={campaignData.recipient_type === "manual"}
                        onChange={(e) =>
                          setCampaignData({
                            ...campaignData,
                            recipient_type: e.target.value,
                          })
                        }
                      />
                      <label className="form-check-label">Manual List</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="recipient_type"
                        value="all_active"
                        checked={campaignData.recipient_type === "all_active"}
                        onChange={(e) =>
                          setCampaignData({
                            ...campaignData,
                            recipient_type: e.target.value,
                          })
                        }
                      />
                      <label className="form-check-label">All Active</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="recipient_type"
                        value="by_role"
                        checked={campaignData.recipient_type === "by_role"}
                        onChange={(e) =>
                          setCampaignData({
                            ...campaignData,
                            recipient_type: e.target.value,
                          })
                        }
                      />
                      <label className="form-check-label">By Role</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="recipient_type"
                        value="selected_ids"
                        checked={campaignData.recipient_type === "selected_ids"}
                        onChange={(e) =>
                          setCampaignData({
                            ...campaignData,
                            recipient_type: e.target.value,
                          })
                        }
                      />
                      <label className="form-check-label">Selected</label>
                    </div>
                  </div>
                </div>
              </div>

              {campaignData.recipient_type === "manual" && (
                <div className="mb-3">
                  <label className="form-label">Email Addresses</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={campaignData.manual_emails}
                    onChange={(e) =>
                      setCampaignData({
                        ...campaignData,
                        manual_emails: e.target.value,
                      })
                    }
                    placeholder="Enter email addresses separated by commas or new lines"
                  />
                </div>
              )}

              {campaignData.recipient_type === "by_role" && (
                <div className="mb-3">
                  <label className="form-label">Select Role</label>
                  <select
                    className="form-select"
                    value={campaignData.role_filter}
                    onChange={(e) =>
                      setCampaignData({
                        ...campaignData,
                        role_filter: e.target.value,
                      })
                    }
                  >
                    <option value="">Select a role...</option>
                    <option value="crp_vendor">
                      CRP Vendors ({subscriberStats.crp_vendors || 0})
                    </option>
                    <option value="state_agency">
                      State Agencies ({subscriberStats.state_agencies || 0})
                    </option>
                    <option value="job_coach">
                      Job Coaches ({subscriberStats.job_coaches || 0})
                    </option>
                  </select>
                </div>
              )}

              {campaignData.recipient_type === "selected_ids" && (
                <div className="alert alert-info">
                  {selectedSubscribers.size} subscribers selected. Go to the
                  "Subscribers" tab to select recipients.
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>
                    Estimated Recipients: {getEstimatedRecipients()}
                  </strong>
                </div>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={sendCampaign}
                  disabled={sending || getEstimatedRecipients() === 0}
                >
                  {sending ? "Sending..." : `Send Campaign`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Subscriber Management</h5>
              <div className="d-flex gap-2 align-items-center">
                <button
                  className="btn btn-success"
                  onClick={() => setShowAddSubscriber(!showAddSubscriber)}
                  style={{ minWidth: "140px" }}
                >
                  {showAddSubscriber ? "Cancel" : "Add Subscriber"}
                </button>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: "120px" }}
                  value={subscriberFilters.active_only}
                  onChange={(e) =>
                    setSubscriberFilters({
                      ...subscriberFilters,
                      active_only: e.target.value,
                    })
                  }
                >
                  <option value="all">All Users</option>
                  <option value="true">Active Only</option>
                  <option value="false">Unsubscribed Only</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: "120px" }}
                  value={subscriberFilters.role}
                  onChange={(e) =>
                    setSubscriberFilters({
                      ...subscriberFilters,
                      role: e.target.value,
                    })
                  }
                >
                  <option value="all">All Roles</option>
                  <option value="crp_vendor">CRP Vendors</option>
                  <option value="state_agency">State Agencies</option>
                  <option value="job_coach">Job Coaches</option>
                </select>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ minWidth: "150px" }}
                  placeholder="Search..."
                  value={subscriberFilters.search}
                  onChange={(e) =>
                    setSubscriberFilters({
                      ...subscriberFilters,
                      search: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            {/* Add Subscriber Form */}
            {showAddSubscriber && (
              <div className="card-body border-bottom">
                <h6>Add New Subscriber</h6>
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={newSubscriber.email}
                        onChange={(e) =>
                          setNewSubscriber({
                            ...newSubscriber,
                            email: e.target.value,
                          })
                        }
                        placeholder="user@example.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newSubscriber.name}
                        onChange={(e) =>
                          setNewSubscriber({
                            ...newSubscriber,
                            name: e.target.value,
                          })
                        }
                        placeholder="John Smith"
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Organization</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newSubscriber.organization}
                        onChange={(e) =>
                          setNewSubscriber({
                            ...newSubscriber,
                            organization: e.target.value,
                          })
                        }
                        placeholder="ABC Rehabilitation"
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-3">
                      <label className="form-label">Role</label>
                      <select
                        className="form-select"
                        value={newSubscriber.role}
                        onChange={(e) =>
                          setNewSubscriber({
                            ...newSubscriber,
                            role: e.target.value,
                          })
                        }
                      >
                        <option value="crp_vendor">CRP Vendor</option>
                        <option value="state_agency">State Agency</option>
                        <option value="job_coach">Job Coach</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={addSubscriber}
                    disabled={addingSubscriber || !newSubscriber.email.trim()}
                  >
                    {addingSubscriber ? "Adding..." : "Add Subscriber"}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddSubscriber(false);
                      setNewSubscriber({
                        email: "",
                        name: "",
                        organization: "",
                        role: "crp_vendor",
                      });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="card-body">
              {loading ? (
                <div className="text-center">Loading...</div>
              ) : (
                <>
                  {/* Bulk Import Instructions */}
                  <div className="alert alert-info mb-3">
                    <h6>Bulk Import:</h6>
                    <p className="mb-2 small">
                      Need to add many subscribers? Contact forms and demo
                      requests are automatically added to your subscriber list.
                      You can also use the contact form or demo form on your
                      website to add subscribers manually.
                    </p>
                    <div className="d-flex gap-2">
                      <a
                        href="/contact"
                        target="_blank"
                        className="btn btn-outline-primary btn-sm"
                      >
                        Contact Form
                      </a>
                      <a
                        href="/demo"
                        target="_blank"
                        className="btn btn-outline-success btn-sm"
                      >
                        Demo Request Form
                      </a>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={
                                selectedSubscribers.size ===
                                  subscribers.filter((s) => s.is_active)
                                    .length &&
                                subscribers.filter((s) => s.is_active).length >
                                  0
                              }
                              onChange={(e) =>
                                selectAllSubscribers(e.target.checked)
                              }
                            />
                          </th>
                          <th>Status</th>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Organization</th>
                          <th>Role</th>
                          <th>Source</th>
                          <th>Subscribed</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map((subscriber) => (
                          <tr
                            key={subscriber.id}
                            className={
                              !subscriber.is_active ? "table-secondary" : ""
                            }
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedSubscribers.has(subscriber.id)}
                                onChange={(e) =>
                                  handleSubscriberSelect(
                                    subscriber.id,
                                    e.target.checked
                                  )
                                }
                                disabled={!subscriber.is_active}
                              />
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  subscriber.is_active
                                    ? "bg-success"
                                    : "bg-danger"
                                }`}
                              >
                                {subscriber.is_active
                                  ? "Subscribed"
                                  : "Unsubscribed"}
                              </span>
                            </td>
                            <td
                              className={
                                !subscriber.is_active ? "text-muted" : ""
                              }
                            >
                              {subscriber.email}
                            </td>
                            <td
                              className={
                                !subscriber.is_active ? "text-muted" : ""
                              }
                            >
                              {subscriber.name || "-"}
                            </td>
                            <td
                              className={
                                !subscriber.is_active ? "text-muted" : ""
                              }
                            >
                              {subscriber.organization || "-"}
                            </td>
                            <td>
                              <span className="badge bg-secondary">
                                {subscriber.role || "unspecified"}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {subscriber.source || "unknown"}
                              </span>
                            </td>
                            <td
                              className={
                                !subscriber.is_active ? "text-muted" : ""
                              }
                            >
                              {new Date(
                                subscriber.subscribed_at
                              ).toLocaleDateString()}
                            </td>
                            <td>
                              <button
                                className={`btn btn-sm ${
                                  subscriber.is_active
                                    ? "btn-outline-danger"
                                    : "btn-outline-success"
                                }`}
                                onClick={() =>
                                  toggleSubscriberStatus(
                                    subscriber.id,
                                    subscriber.is_active
                                  )
                                }
                                title={
                                  subscriber.is_active
                                    ? "Unsubscribe user"
                                    : "Resubscribe user"
                                }
                              >
                                {subscriber.is_active
                                  ? "Unsubscribe"
                                  : "Resubscribe"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {subscribers.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-muted">
                          No subscribers found. Add your first subscriber above!
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === "campaigns" && (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Campaign History</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center">Loading...</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Subject</th>
                        <th>Recipients</th>
                        <th>Opens</th>
                        <th>Clicks</th>
                        <th>Date Sent</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign) => (
                        <tr key={campaign.id}>
                          <td>{campaign.campaign_name}</td>
                          <td>{campaign.subject_line}</td>
                          <td>{campaign.recipients_count}</td>
                          <td>{campaign.opens || 0}</td>
                          <td>{campaign.clicks || 0}</td>
                          <td>
                            {campaign.sent_at
                              ? new Date(campaign.sent_at).toLocaleDateString()
                              : "-"}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                campaign.status === "sent"
                                  ? "bg-success"
                                  : campaign.status === "sending"
                                  ? "bg-warning"
                                  : "bg-secondary"
                              }`}
                            >
                              {campaign.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Template Tab */}
        {activeTab === "template" && (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Email Template</h5>
            </div>
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-8">
                  <h6>WebVenly Marketing Email Template</h6>
                  <p className="text-muted">
                    Professional HTML email template with WebVenly branding,
                    feature highlights, and automatic unsubscribe links.
                  </p>
                </div>
                <div className="col-md-4 text-end">
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={previewEmail}
                      disabled={loadingTemplate}
                    >
                      {loadingTemplate
                        ? "Loading..."
                        : showPreview
                        ? "Hide Preview"
                        : "Show Preview"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={downloadTemplate}
                      disabled={loadingTemplate}
                    >
                      Download HTML
                    </button>
                  </div>
                </div>
              </div>

              {/* Template Preview */}
              {showPreview && emailHTML && (
                <div className="mb-4">
                  <h6>Email Preview:</h6>
                  <div
                    className="border rounded p-3"
                    style={{
                      maxHeight: "600px",
                      overflowY: "auto",
                      backgroundColor: "#f8f9fa",
                    }}
                    dangerouslySetInnerHTML={{ __html: emailHTML }}
                  />
                </div>
              )}

              {/* Template Usage Instructions */}
              <div className="row">
                <div className="col-md-6">
                  <div className="card border-info">
                    <div className="card-header bg-info bg-opacity-10">
                      <h6 className="mb-0">For Email Marketing Platforms:</h6>
                    </div>
                    <div className="card-body">
                      <ol className="small">
                        <li>Download the HTML template</li>
                        <li>
                          Open your email platform (Mailchimp, Constant Contact,
                          etc.)
                        </li>
                        <li>Choose "HTML" or "Code" editor</li>
                        <li>Paste the code</li>
                        <li>Update links to match your URLs</li>
                        <li>Test and send!</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-warning">
                    <div className="card-header bg-warning bg-opacity-10">
                      <h6 className="mb-0">For Manual Sending:</h6>
                    </div>
                    <div className="card-body">
                      <ol className="small">
                        <li>Download the HTML file</li>
                        <li>Open in web browser</li>
                        <li>Copy the rendered email</li>
                        <li>Paste into Outlook as formatted text</li>
                        <li>Or use Outlook's HTML import feature</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 p-3 bg-light rounded">
                <h6>Suggested Subject Lines:</h6>
                <ul className="small mb-0">
                  <li>
                    "Stop Drowning in Paperwork - See How [Company] Saves 10+
                    Hours/Week"
                  </li>
                  <li>"Finally: CRP Software That Actually Works"</li>
                  <li>"Cut Your Paperwork in Half - Free Demo Inside"</li>
                  <li>"Transform Your CRP Operations in 30 Days"</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Unsubscribed Tab */}
        {activeTab === "unsubscribed" && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Unsubscribed Users</h5>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ minWidth: "200px" }}
                  placeholder="Search email addresses..."
                  value={subscriberFilters.search}
                  onChange={(e) =>
                    setSubscriberFilters({
                      ...subscriberFilters,
                      search: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center">Loading...</div>
              ) : (
                <>
                  <div className="alert alert-warning mb-3">
                    <h6>Compliance Information:</h6>
                    <p className="mb-2 small">
                      These users have unsubscribed from marketing emails and
                      are automatically excluded from all campaigns. Do not
                      manually add them back to campaigns without explicit
                      re-consent.
                    </p>
                    <p className="mb-0 small">
                      <strong>Legal Note:</strong> CAN-SPAM Act requires
                      honoring unsubscribe requests within 10 business days.
                    </p>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Email Address</th>
                          <th>Unsubscribe Method</th>
                          <th>Date Unsubscribed</th>
                          <th>Reason</th>
                          <th>IP Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unsubscribed.map((user, index) => (
                          <tr key={index}>
                            <td>{user.email}</td>
                            <td>
                              <span
                                className={`badge ${
                                  user.method === "link"
                                    ? "bg-primary"
                                    : user.method === "form"
                                    ? "bg-info"
                                    : "bg-secondary"
                                }`}
                              >
                                {user.method || "unknown"}
                              </span>
                            </td>
                            <td>
                              {new Date(
                                user.unsubscribed_at
                              ).toLocaleDateString()}
                            </td>
                            <td>{user.reason || "-"}</td>
                            <td className="small text-muted">
                              {user.ip_address || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {unsubscribed.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-muted">
                          No unsubscribed users found. This is good news!
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Summary Statistics */}
                  {unsubscribed.length > 0 && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <div className="row text-center">
                        <div className="col-md-3">
                          <strong>{unsubscribed.length}</strong>
                          <div className="small text-muted">
                            Total Unsubscribed
                          </div>
                        </div>
                        <div className="col-md-3">
                          <strong>
                            {
                              unsubscribed.filter((u) => u.method === "link")
                                .length
                            }
                          </strong>
                          <div className="small text-muted">Via Email Link</div>
                        </div>
                        <div className="col-md-3">
                          <strong>
                            {
                              unsubscribed.filter((u) => u.method === "form")
                                .length
                            }
                          </strong>
                          <div className="small text-muted">
                            Via Website Form
                          </div>
                        </div>
                        <div className="col-md-3">
                          <strong>
                            {subscriberStats.active_subscribers &&
                            subscriberStats.total_unsubscribes
                              ? (
                                  (subscriberStats.total_unsubscribes /
                                    (subscriberStats.active_subscribers +
                                      subscriberStats.total_unsubscribes)) *
                                  100
                                ).toFixed(1)
                              : 0}
                            %
                          </strong>
                          <div className="small text-muted">
                            Unsubscribe Rate
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default EmailMarketing;
